import React, { useState, useEffect, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLightbulb,
  faHandshake,
  faHandHoldingHeart,
  faUsersViewfinder,
  faUserShield,
} from "@fortawesome/free-solid-svg-icons";

const coreValues = [
  {
    icon: faLightbulb,
    title: "Innovation",
    content:
      "We strive to lead in various mission - critical applications by continually developing cutting-edge solutions.",
  },
  {
    icon: faHandshake,
    title: "Integrity",
    content: "We build trust through honesty, transparency, and ethical business practices.",
  },
  {
    icon: faHandHoldingHeart,
    title: "Excellence",
    content: "We deliver high-quality solutions that exceed customer expectations.",
  },
  {
    icon: faUsersViewfinder,
    title: "Customer Focus",
    content: "Our customers' needs drive everything we do, ensuring tailored and impactful solutions.",
  },
  {
    icon: faUserShield,
    title: "Safety",
    content: "We prioritize safety in all aspects of our products and operations.",
  },
];

const CoreValue = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const observerRef = useRef(null); // Store observer instance

  const handleIntersection = useCallback(([entry]) => {
    if (entry.isIntersecting) {
      setIsVisible(true);
      observerRef.current?.disconnect(); // Stop observing once visible
    }
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold: 0.1,
    });

    const section = sectionRef.current;
    if (section) observerRef.current.observe(section);

    return () => observerRef.current?.disconnect();
  }, [handleIntersection]);

  return (
    <div className="pt-32 pb-4 mb-6 mt-28 md:mt-6 lg:mt-6">
      {/* Section Heading */}
      <div className="text-center mb-12">
        <h3 className="text-4xl mt-12 text-primary font-bold relative inline-block">
          Core
          <span className="absolute bottom-[-8px] left-0 w-12 h-1 bg-secondary"></span>
          <span className="text-secondary"> Values</span>
        </h3>
        <p className="text-center my-8 w-[60%] mx-auto">
          Our core values of innovation, integrity, excellence, customer focus, and safety drive
          everything we do. They guide our commitment to delivering high-quality, impactful solutions
          and building strong, lasting relationships with our clients and partners.
        </p>
      </div>

      {/* Values Grid */}
      <div ref={sectionRef} className="container mx-auto grid grid-cols-1 md:grid-cols-5 gap-4 lg:gap-8 px-2">
        {coreValues.map((value, index) => (
          <div
            key={index}
            className={`flex flex-col text-center p-4 mx-8 sm:mx-18 lg:mx-0 md:mx-0 shadow-md rounded-md bg-primary transition-opacity duration-1000 ${
              isVisible
                ? index % 2 === 0
                  ? "opacity-100 translate-y-0 even-slide-in"
                  : "opacity-100 translate-y-0 md:translate-y-8 sm:translate-y-0 odd-slide-in"
                : "opacity-0 translate-y-10"
            }`}
          >
            <div className="text-white text-4xl mb-4">
              <FontAwesomeIcon icon={value.icon} />
            </div>
            <h4 className="text-md lg:text-xl font-semibold text-white mb-2">{value.title}</h4>
            <p className="text-white text-center text-sm md:text-base">{value.content}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CoreValue;
